import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Loader2,
  ClipboardList,
  StickyNote,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Pencil,
  Plus,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { formatDateForDisplay, formatDateToISO } from '../utils/dateUtils';
import { getDefaultHotboxDateRange } from '../utils/hotbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Input,
  Button,
  Badge,
  DatePicker,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import {
  getMyTasksTasksMyTasksGet,
  updateTaskNotesTasksTaskIdNotesPut,
} from '../services/api/sdk.gen';
import type {
  HotboxTaskResponse,
  TaskNotesResponse,
} from '../services/api/types.gen';
import type { VisitTypeId } from '../services/api/types.gen';
import type { SortBy } from '../services/api/models/SortBy';
import type { SortDirection } from '../services/api/models/SortDirection';
import { supportsActiveDiagnosisPage } from '../constants/visitTypes';
import {
  ALL_CLINICS,
  useAccessibleClinics,
} from '../hooks/useAccessibleClinics';
import { ClinicSelect } from '../components/clinic/ClinicSelect';
import { HotboxTaskNotesDialog } from '../components/hotbox/HotboxTaskNotesDialog';
import { getDefaultLandingPath } from '../utils/defaultLandingPath';
import { PermissionSlug } from '../constants/featureFlags';

type RoleFilter = 'all' | 'clinician' | 'case_manager';
type QAScope = 'all' | 'my';
type HotboxScope = 'clinician' | 'qa';

const PAGE_SIZE = 50;

const DEFAULT_SORT_DIRECTION: Record<SortBy, SortDirection> = {
  visit_date: 'desc',
  status: 'desc',
  patient_name: 'asc',
  clinic_name: 'asc',
  visit_type: 'asc',
  assigned: 'asc',
};

type SortableColumn = {
  column: SortBy;
  label: string;
  className: string;
  allClinicsOnly?: boolean;
};

const SORTABLE_COLUMNS: SortableColumn[] = [
  { column: 'visit_date', label: 'Date', className: 'w-28' },
  { column: 'patient_name', label: 'Patient', className: 'w-48' },
  {
    column: 'clinic_name',
    label: 'Clinic',
    className: 'w-40',
    allClinicsOnly: true,
  },
  { column: 'visit_type', label: 'Type', className: 'w-40' },
  { column: 'status', label: 'Status', className: 'w-40' },
  { column: 'assigned', label: 'Assigned', className: 'w-56' },
];

const TASK_NOTES_SAVE_ERROR = 'Failed to save task notes.';

type ConfirmedNotesOverlay = {
  notes: string;
  status: HotboxTaskResponse['status'];
  throughFetchId: number;
};

function normalizeNotes(notes: string | null | undefined): string {
  return notes ?? '';
}

function hasGeneratedClientError(
  response: Awaited<ReturnType<typeof updateTaskNotesTasksTaskIdNotesPut>>
): boolean {
  return 'error' in response && response.error != null;
}

function assignmentLabel(task: HotboxTaskResponse): string {
  if (
    task.is_case_manager_task &&
    !task.is_clinician_task &&
    task.case_manager_name
  ) {
    return task.case_manager_name;
  }

  return task.assigned_clinician_name || task.case_manager_name || '—';
}

const STATUS_BADGE_VARIANT: Record<
  string,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  not_started: 'outline',
  saved: 'secondary',
  submitted: 'default',
  submitted_with_signature: 'default',
  pending_clinician_signature: 'destructive',
  completed: 'secondary',
};

const STATUS_LABEL: Record<string, string> = {
  not_started: 'Not Started',
  saved: 'Saved',
  submitted: 'Submitted',
  submitted_with_signature: 'Submitted (Signed)',
  pending_clinician_signature: 'Pending Signature',
  completed: 'Completed',
  cancelled: 'Cancelled',
  pending: 'Pending',
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
};

function formatVisitType(visitType: string): string {
  return visitType
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .replace(/\bOasis\b/i, 'OASIS')
    .replace(/\bSoc\b/i, 'SOC')
    .replace(/\bRoc\b/i, 'ROC')
    .replace(/\bRn\b/i, 'RN')
    .replace(/\bLpn\b/i, 'LPN')
    .replace(/\bPt\b/i, 'PT')
    .replace(/\bOt\b/i, 'OT')
    .replace(/\bSt\b/i, 'ST')
    .replace(/\bMsw\b/i, 'MSW')
    .replace(/\bHha\b/i, 'HHA');
}

type SortableTableHeadProps = {
  column: SortBy;
  sortField: SortBy;
  sortDirection: SortDirection;
  onSort: (column: SortBy) => void;
  className?: string;
  children: React.ReactNode;
};

const SortableTableHead: React.FC<SortableTableHeadProps> = ({
  column,
  sortField,
  sortDirection,
  onSort,
  className,
  children,
}) => (
  <TableHead className={className}>
    <button
      type='button'
      className='flex w-full items-center gap-1 text-left font-medium hover:text-foreground'
      onClick={() => onSort(column)}
    >
      <span>{children}</span>
      {sortField === column ? (
        sortDirection === 'asc' ? (
          <ArrowUp className='h-3.5 w-3.5 shrink-0' />
        ) : (
          <ArrowDown className='h-3.5 w-3.5 shrink-0' />
        )
      ) : (
        <ArrowDown className='h-3.5 w-3.5 shrink-0 opacity-30' />
      )}
    </button>
  </TableHead>
);

type TaskNotesTriggerProps = {
  task: HotboxTaskResponse;
  onOpen: (
    task: HotboxTaskResponse,
    event: React.MouseEvent<HTMLButtonElement>
  ) => void;
};

const TaskNotesTrigger: React.FC<TaskNotesTriggerProps> = ({
  task,
  onOpen,
}) => {
  const noteRef = useRef<HTMLSpanElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const updateTruncation = useCallback(() => {
    const note = noteRef.current;
    setIsTruncated(Boolean(note && note.scrollWidth > note.clientWidth));
  }, []);

  useEffect(() => {
    updateTruncation();
  }, [task.notes, updateTruncation]);

  if (!task.notes) {
    return (
      <button
        type='button'
        className='group flex cursor-pointer items-center gap-1 text-left text-muted-foreground hover:text-foreground'
        aria-label={`Add task notes for ${task.patient_name}`}
        onClick={event => onOpen(task, event)}
      >
        <span>Add note</span>
        <Plus className='h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-70 group-focus-visible:opacity-70' />
      </button>
    );
  }

  const trigger = (
    <button
      type='button'
      className='group flex min-w-0 max-w-full cursor-pointer items-center gap-1 text-left text-muted-foreground hover:text-foreground'
      aria-label={`Edit task notes for ${task.patient_name}`}
      onClick={event => onOpen(task, event)}
      onFocus={updateTruncation}
      onMouseEnter={updateTruncation}
    >
      <span ref={noteRef} className='truncate'>
        {task.notes}
      </span>
      <Pencil className='h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-70 group-focus-visible:opacity-70' />
    </button>
  );

  if (!isTruncated) {
    return trigger;
  }

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent side='top' className='max-w-sm whitespace-pre-line'>
          {task.notes}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const Hotbox: React.FC = () => {
  const navigate = useNavigate();
  const { showError, user, hasPermission, hasAnyPermission, hasFeatureFlag } =
    useStore(
      useShallow(state => ({
        showError: state.showError,
        user: state.user,
        hasPermission: state.hasPermission,
        hasAnyPermission: state.hasAnyPermission,
        hasFeatureFlag: state.hasFeatureFlag,
      }))
    );

  useEffect(() => {
    if (
      !hasPermission(PermissionSlug.EMR_MANAGE) &&
      !hasPermission(PermissionSlug.HOTBOX_ACCESS) &&
      !hasPermission(PermissionSlug.QA_ACCESS)
    ) {
      navigate(
        getDefaultLandingPath({
          hasPermission,
          hasAnyPermission,
          hasFeatureFlag,
        })
      );
    }
  }, [hasAnyPermission, hasFeatureFlag, hasPermission, navigate]);

  const userClinics = user?.user_clinics ?? [];

  const {
    clinics: clinicOptions,
    selectedClinicId,
    setSelectedClinicId,
    isAllClinics,
  } = useAccessibleClinics();

  const [tasks, setTasks] = useState<HotboxTaskResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeScope, setActiveScope] = useState<HotboxScope>('clinician');
  const [qaScope, setQAScope] = useState<QAScope>('all');
  const [isQAScopeMenuOpen, setIsQAScopeMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState<SortBy>('visit_date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [dateFrom, setDateFrom] = useState<Date | undefined>(
    () => getDefaultHotboxDateRange().from
  );
  const [dateTo, setDateTo] = useState<Date | undefined>(
    () => getDefaultHotboxDateRange().to
  );
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [confirmedNotes, setConfirmedNotes] = useState('');
  const [draftNotes, setDraftNotes] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [taskNotesError, setTaskNotesError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);
  const confirmedNotesOverlayRef = useRef<Map<string, ConfirmedNotesOverlay>>(
    new Map()
  );

  const selectedUserClinic = useMemo(
    () =>
      isAllClinics
        ? undefined
        : clinicOptions.find(c => c.id === selectedClinicId)?.userClinic,
    [clinicOptions, selectedClinicId, isAllClinics]
  );

  const aggregateRoles = useMemo(() => {
    return {
      isClinician: userClinics.some(uc => uc.is_assignable_clinician),
      isCaseManager: userClinics.some(uc => uc.is_case_manager),
    };
  }, [userClinics]);

  const isClinicianScopeAvailable = isAllClinics
    ? aggregateRoles.isClinician
    : (selectedUserClinic?.is_assignable_clinician ?? false);
  const isCaseManagerScopeAvailable = isAllClinics
    ? aggregateRoles.isCaseManager
    : (selectedUserClinic?.is_case_manager ?? false);
  const isQAScopeAvailable =
    hasPermission(PermissionSlug.QA_ACCESS) || isCaseManagerScopeAvailable;
  const hasAnyRole = isClinicianScopeAvailable || isQAScopeAvailable;
  const roleFilter: RoleFilter =
    activeScope === 'qa' ? 'case_manager' : 'clinician';

  const isDateRangeInvalid = Boolean(dateFrom && dateTo && dateFrom > dateTo);

  function handleSort(column: SortBy) {
    if (sortField === column) {
      setSortDirection(currentDirection =>
        currentDirection === 'asc' ? 'desc' : 'asc'
      );
      return;
    }

    setSortField(column);
    setSortDirection(DEFAULT_SORT_DIRECTION[column]);
  }

  const applyConfirmedNotesOverlay = useCallback(
    (fetchedTasks: HotboxTaskResponse[], fetchId: number) => {
      // Saving notes can race an already-started list refresh. Keep the
      // confirmed PUT response visible until that in-flight GET has had one
      // chance to settle, then trust the next server snapshot again.
      const overlayMap = confirmedNotesOverlayRef.current;
      if (overlayMap.size === 0) return fetchedTasks;

      const tasksWithOverlay = fetchedTasks.map(task => {
        const overlay = overlayMap.get(task.id);
        if (!overlay) return task;
        if (fetchId > overlay.throughFetchId) {
          overlayMap.delete(task.id);
          return task;
        }
        if (fetchId === overlay.throughFetchId) {
          overlayMap.delete(task.id);
        }
        return {
          ...task,
          notes: overlay.notes,
          status: overlay.status,
        };
      });

      for (const [taskId, overlay] of overlayMap) {
        if (fetchId > overlay.throughFetchId) {
          overlayMap.delete(taskId);
        }
      }

      return tasksWithOverlay;
    },
    []
  );

  useEffect(() => {
    if (activeScope === 'clinician' && !isClinicianScopeAvailable) {
      setActiveScope(isQAScopeAvailable ? 'qa' : 'clinician');
      return;
    }
    if (activeScope === 'qa' && !isQAScopeAvailable) {
      setActiveScope(isClinicianScopeAvailable ? 'clinician' : 'qa');
    }
  }, [activeScope, isClinicianScopeAvailable, isQAScopeAvailable]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(0);
  }, [
    activeScope,
    qaScope,
    debouncedSearch,
    selectedClinicId,
    sortField,
    sortDirection,
    dateFrom,
    dateTo,
  ]);

  useEffect(() => {
    if (isDateRangeInvalid) {
      setLoading(false);
      setTasks([]);
      setHasMore(false);
      return;
    }

    const id = ++fetchIdRef.current;
    setLoading(true);

    getMyTasksTasksMyTasksGet({
      query: {
        role_filter: roleFilter,
        qa_scope: activeScope === 'qa' ? qaScope : 'my',
        search: debouncedSearch || '',
        clinic_id: isAllClinics ? undefined : selectedClinicId || undefined,
        date_from: dateFrom ? formatDateToISO(dateFrom) : undefined,
        date_to: dateTo ? formatDateToISO(dateTo) : undefined,
        sort_by: sortField,
        sort_direction: sortDirection,
        limit: PAGE_SIZE + 1,
        offset: page * PAGE_SIZE,
      },
    })
      .then(response => {
        if (id !== fetchIdRef.current) return;
        const data = Array.isArray(response.data) ? response.data : [];
        setHasMore(data.length > PAGE_SIZE);
        setTasks(applyConfirmedNotesOverlay(data.slice(0, PAGE_SIZE), id));
      })
      .catch(() => {
        if (id !== fetchIdRef.current) return;
        setTasks([]);
        showError('Failed to load tasks');
      })
      .finally(() => {
        if (id === fetchIdRef.current) {
          setLoading(false);
        }
      });
  }, [
    roleFilter,
    activeScope,
    qaScope,
    debouncedSearch,
    selectedClinicId,
    isAllClinics,
    page,
    sortField,
    sortDirection,
    dateFrom,
    dateTo,
    isDateRangeInvalid,
    showError,
    applyConfirmedNotesOverlay,
  ]);

  const handleRowClick = (task: HotboxTaskResponse, e: React.MouseEvent) => {
    const path = `/patients/${task.patient_id}/visits/${task.id}`;
    if (e.metaKey || e.ctrlKey) {
      window.open(path, '_blank');
    } else {
      navigate(path);
    }
  };

  const resetTaskNotesDialog = () => {
    setSelectedTaskId(null);
    setConfirmedNotes('');
    setDraftNotes('');
    setTaskNotesError(null);
  };

  const openTaskNotesDialog = (
    task: HotboxTaskResponse,
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    const notes = normalizeNotes(task.notes);
    setSelectedTaskId(task.id);
    setConfirmedNotes(notes);
    setDraftNotes(notes);
    setTaskNotesError(null);
  };

  const handleSaveTaskNotes = async () => {
    if (!selectedTaskId || isSavingNotes || draftNotes === confirmedNotes) {
      return;
    }

    setIsSavingNotes(true);
    setTaskNotesError(null);

    try {
      const response = await updateTaskNotesTasksTaskIdNotesPut({
        path: { task_id: selectedTaskId },
        body: { notes: draftNotes === '' ? null : draftNotes },
      });

      if (hasGeneratedClientError(response) || !response.data) {
        throw response;
      }

      const data: TaskNotesResponse = response.data;
      // Remember the current list fetch generation so a stale response cannot
      // make the note look unsaved immediately after the user clicks Save.
      confirmedNotesOverlayRef.current.set(data.id, {
        notes: data.notes,
        status: data.status,
        throughFetchId: fetchIdRef.current,
      });
      setTasks(currentTasks =>
        currentTasks.map(task =>
          task.id === data.id
            ? { ...task, notes: data.notes, status: data.status }
            : task
        )
      );
      resetTaskNotesDialog();
    } catch (error) {
      console.error('Failed to save task notes', error);
      setTaskNotesError(TASK_NOTES_SAVE_ERROR);
    } finally {
      setIsSavingNotes(false);
    }
  };

  const handleClinicChange = (clinicId: string) => {
    setSelectedClinicId(clinicId);
    setSearchTerm('');
  };

  if (!hasAnyRole) {
    return (
      <div className='space-y-4'>
        <div className='flex items-center gap-2'>
          <ClipboardList className='h-6 w-6 text-primary' />
          <h1 className='text-2xl font-bold'>Hotbox</h1>
        </div>
        <div className='rounded-md border p-12 text-center text-muted-foreground'>
          <ClipboardList className='mx-auto mb-3 h-10 w-10 text-muted-foreground/50' />
          <p className='text-lg font-medium'>No tasks to show</p>
          <p className='mt-1 text-sm'>
            The Hotbox displays clinician and QA tasks. You are not currently
            assigned a supported Hotbox role at any clinic.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <ClipboardList className='h-6 w-6 text-primary' />
          <h1 className='text-2xl font-bold'>Hotbox</h1>
        </div>
      </div>

      <div className='flex flex-wrap items-center gap-3'>
        <ClinicSelect
          className='w-56'
          selectSize='sm'
          clinics={clinicOptions}
          includeAllOption
          allOptionValue={ALL_CLINICS}
          value={selectedClinicId}
          onChange={e => handleClinicChange(e.target.value)}
          placeholder='Select clinic...'
        />

        <div className='relative'>
          <Search className='absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
          <Input
            placeholder='Search by patient name...'
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className='pl-9 w-full md:w-72'
            inputSize='sm'
          />
        </div>

        {(isClinicianScopeAvailable || isQAScopeAvailable) && (
          <div className='flex h-7 overflow-visible rounded-md border border-border'>
            {isClinicianScopeAvailable && (
              <button
                type='button'
                onClick={() => {
                  setActiveScope('clinician');
                  setIsQAScopeMenuOpen(false);
                }}
                className={`flex items-center px-2 text-xs font-medium transition-colors ${
                  activeScope === 'clinician'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background hover:bg-accent text-foreground'
                } ${isQAScopeAvailable ? 'rounded-l-md' : 'rounded-md'}`}
              >
                Clinician
              </button>
            )}

            {isQAScopeAvailable && (
              <div className='relative flex'>
                <button
                  type='button'
                  onClick={() => setActiveScope('qa')}
                  className={`flex items-center px-2 text-xs font-medium transition-colors ${
                    activeScope === 'qa'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background hover:bg-accent text-foreground'
                  } ${isClinicianScopeAvailable ? '' : 'rounded-l-md'}`}
                >
                  {qaScope === 'all' ? 'QA: All Tasks' : 'QA: My Tasks'}
                </button>
                <button
                  type='button'
                  aria-label='Choose QA task scope'
                  aria-haspopup='menu'
                  aria-expanded={isQAScopeMenuOpen}
                  onClick={() => {
                    setActiveScope('qa');
                    setIsQAScopeMenuOpen(open => !open);
                  }}
                  className={`flex items-center rounded-r-md border-l border-border px-1 transition-colors ${
                    activeScope === 'qa'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background hover:bg-accent text-foreground'
                  }`}
                >
                  <ChevronDown className='h-3 w-3' />
                </button>

                {isQAScopeMenuOpen && (
                  <div
                    role='menu'
                    className='absolute right-0 top-full z-20 mt-1 min-w-24 rounded-md border bg-popover p-1 text-popover-foreground shadow-md'
                  >
                    {[
                      { label: 'QA: All Tasks', value: 'all' as const },
                      { label: 'QA: My Tasks', value: 'my' as const },
                    ].map(option => (
                      <button
                        key={option.value}
                        type='button'
                        role='menuitemradio'
                        aria-checked={qaScope === option.value}
                        onClick={() => {
                          setActiveScope('qa');
                          setQAScope(option.value);
                          setIsQAScopeMenuOpen(false);
                        }}
                        className={`block w-full rounded-sm px-2 py-1 text-left text-xs hover:bg-accent ${
                          qaScope === option.value ? 'font-semibold' : ''
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <DatePicker
          datePickerSize='sm'
          showClearButton
          placeholder='From date'
          date={dateFrom}
          onDateChange={setDateFrom}
          className='w-full md:w-36'
        />
        <DatePicker
          datePickerSize='sm'
          showClearButton
          placeholder='To date'
          date={dateTo}
          onDateChange={setDateTo}
          className='w-full md:w-36'
        />

        {loading && (
          <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
        )}
      </div>

      <div className='rounded-md border'>
        <Table>
          <TableHeader>
            <TableRow>
              {SORTABLE_COLUMNS.filter(
                column => !column.allClinicsOnly || isAllClinics
              ).map(column => (
                <SortableTableHead
                  key={column.column}
                  column={column.column}
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  className={column.className}
                >
                  {column.label}
                </SortableTableHead>
              ))}
              <TableHead>
                <div className='flex items-center gap-1'>
                  <StickyNote className='h-3.5 w-3.5' />
                  Notes
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && tasks.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={isAllClinics ? 7 : 6}
                  className='h-32 text-center'
                >
                  <Loader2 className='mx-auto h-6 w-6 animate-spin text-muted-foreground' />
                </TableCell>
              </TableRow>
            ) : !loading && tasks.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={isAllClinics ? 7 : 6}
                  className='h-32 text-center text-muted-foreground'
                >
                  {isDateRangeInvalid
                    ? 'From date must be on or before To date.'
                    : debouncedSearch
                      ? 'No tasks match your search.'
                      : roleFilter === 'case_manager'
                        ? `No ${
                            qaScope === 'my' ? 'my ' : ''
                          }QA tasks found${isAllClinics ? '' : ' for this clinic'}.`
                        : roleFilter === 'clinician'
                          ? `No clinician tasks found${isAllClinics ? '' : ' for this clinic'}.`
                          : `No tasks found${isAllClinics ? '' : ' for this clinic'}.`}
                </TableCell>
              </TableRow>
            ) : (
              tasks.map(task => (
                <TableRow
                  key={task.id}
                  className='cursor-pointer hover:bg-accent/50 transition-colors'
                  onClick={e => handleRowClick(task, e)}
                >
                  <TableCell className='font-medium text-sm'>
                    {formatDateForDisplay(task.visit_date) || '—'}
                  </TableCell>
                  <TableCell className='font-medium text-sm'>
                    {task.patient_name}
                  </TableCell>
                  {isAllClinics && (
                    <TableCell className='text-sm'>
                      {task.clinic_name || '—'}
                    </TableCell>
                  )}
                  <TableCell className='text-sm'>
                    <span className='flex items-center gap-1.5'>
                      {formatVisitType(task.visit_type)}
                      {task.task_type === 'visit' &&
                        supportsActiveDiagnosisPage(
                          task.visit_type as VisitTypeId
                        ) &&
                        task.has_active_diagnosis && (
                          <Badge
                            variant='secondary'
                            className='shrink-0 text-[10px] px-1.5 py-0'
                            title='Active diagnosis entered'
                          >
                            Dx
                          </Badge>
                        )}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={STATUS_BADGE_VARIANT[task.status] ?? 'outline'}
                    >
                      {STATUS_LABEL[task.status] ?? task.status}
                    </Badge>
                  </TableCell>
                  <TableCell className='text-sm text-muted-foreground max-w-xs truncate'>
                    {assignmentLabel(task)}
                  </TableCell>
                  <TableCell className='text-sm text-muted-foreground max-w-xs'>
                    <TaskNotesTrigger
                      task={task}
                      onOpen={openTaskNotesDialog}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className='flex items-center justify-between text-sm text-muted-foreground'>
        <span>
          {tasks.length > 0
            ? `Showing ${page * PAGE_SIZE + 1}–${page * PAGE_SIZE + tasks.length}`
            : 'No results'}
        </span>
        <div className='flex items-center gap-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            <ChevronLeft className='h-4 w-4' />
            Previous
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={() => setPage(p => p + 1)}
            disabled={!hasMore}
          >
            Next
            <ChevronRight className='h-4 w-4' />
          </Button>
        </div>
      </div>

      <HotboxTaskNotesDialog
        open={selectedTaskId !== null}
        draftNotes={draftNotes}
        confirmedNotes={confirmedNotes}
        isSaving={isSavingNotes}
        error={taskNotesError}
        onDraftNotesChange={setDraftNotes}
        onSave={handleSaveTaskNotes}
        onCancel={resetTaskNotesDialog}
      />
    </div>
  );
};

export default Hotbox;
